import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ShieldCheck, UserPlus } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill } from '../componentes/ui/Pill.js';
import { useSessao } from '../auth/SessaoContexto.js';

interface Papel {
  id: string;
  nome: string;
  descricao: string | null;
  escopo: string[];
  sistema: boolean;
}

interface Usuario {
  id: string;
  nome: string;
  email: string;
  tipo: 'INTERNO' | 'CLIENTE';
  ativo: boolean;
  tfaAtivo: boolean;
  ultimoAcesso: string | null;
  operadora: { id: string; nome: string } | null;
  papeis: { id: string; nome: string }[];
  sessoesAtivas: number;
}

interface Operadora {
  id: string;
  nome: string;
}

export function Usuarios() {
  const qc = useQueryClient();
  const { usuario: eu } = useSessao();
  const [novoAberto, setNovoAberto] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState<{ email: string; senha: string } | null>(null);

  const { data: usuarios = [], isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get<Usuario[]>('/usuarios'),
  });
  const { data: papeis = [] } = useQuery({
    queryKey: ['papeis'],
    queryFn: () => api.get<Papel[]>('/usuarios/papeis'),
  });

  const recarregar = () => void qc.invalidateQueries({ queryKey: ['usuarios'] });

  const internos = usuarios.filter((u) => u.tipo === 'INTERNO');
  const clientes = usuarios.filter((u) => u.tipo === 'CLIENTE');

  if (isLoading) return <Esqueleto />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-texto-suave">
          {internos.length} na equipe · {clientes.length} de operadoras
        </p>
        <Botao variante="primario" iconeEsquerda={<UserPlus size={16} />} onClick={() => setNovoAberto(true)}>
          Novo usuário
        </Botao>
      </div>

      {/* Os dois grupos ficam em tabelas separadas, como no desenho original:
          um usuário interno enxerga todas as operadoras e um de cliente só a
          sua. Misturá-los numa lista só facilitaria conceder o papel errado. */}
      <Grupo
        titulo="Equipe Consult Networks"
        subtitulo="Acesso a todas as operadoras, conforme o papel"
        usuarios={internos}
        papeis={papeis}
        meuId={eu?.id}
        aoMudar={recarregar}
        aoGerarSenha={setSenhaGerada}
      />

      <Grupo
        titulo="Usuários de operadoras"
        subtitulo="Cada conta enxerga apenas os blocos da própria operadora"
        usuarios={clientes}
        papeis={papeis}
        meuId={eu?.id}
        aoMudar={recarregar}
        aoGerarSenha={setSenhaGerada}
        mostrarOperadora
      />

      {novoAberto && (
        <ModalNovoUsuario
          papeis={papeis}
          aoFechar={() => setNovoAberto(false)}
          aoCriar={(dados) => {
            recarregar();
            setSenhaGerada(dados);
          }}
        />
      )}

      {senhaGerada && (
        <ModalSenhaGerada dados={senhaGerada} aoFechar={() => setSenhaGerada(null)} />
      )}
    </div>
  );
}

function Grupo({
  titulo,
  subtitulo,
  usuarios,
  papeis,
  meuId,
  aoMudar,
  aoGerarSenha,
  mostrarOperadora,
}: {
  titulo: string;
  subtitulo: string;
  usuarios: Usuario[];
  papeis: Papel[];
  meuId?: string;
  aoMudar: () => void;
  aoGerarSenha: (d: { email: string; senha: string }) => void;
  mostrarOperadora?: boolean;
}) {
  return (
    <section>
      <header className="mb-3">
        <h2 className="text-[15px] font-extrabold">{titulo}</h2>
        <p className="text-[12.5px] text-texto-suave">{subtitulo}</p>
      </header>

      {usuarios.length === 0 ? (
        <p className="rounded-card border border-dashed border-borda bg-superficie px-4 py-8 text-center text-[13px] text-texto-suave">
          Nenhum usuário neste grupo.
        </p>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Usuário', mostrarOperadora ? 'Operadora' : '2FA', 'Papéis', 'Último acesso', ''].map(
                    (h, i) => (
                      <th
                        key={i}
                        className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <Linha
                    key={u.id}
                    usuario={u}
                    papeis={papeis}
                    souEu={u.id === meuId}
                    aoMudar={aoMudar}
                    aoGerarSenha={aoGerarSenha}
                    mostrarOperadora={mostrarOperadora}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function Linha({
  usuario,
  papeis,
  souEu,
  aoMudar,
  aoGerarSenha,
  mostrarOperadora,
}: {
  usuario: Usuario;
  papeis: Papel[];
  souEu: boolean;
  aoMudar: () => void;
  aoGerarSenha: (d: { email: string; senha: string }) => void;
  mostrarOperadora?: boolean;
}) {
  const [editandoPapeis, setEditandoPapeis] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const alternarAtivo = useMutation({
    mutationFn: () => api.patch(`/usuarios/${usuario.id}/ativo`, { ativo: !usuario.ativo }),
    onSuccess: () => {
      setErro(null);
      aoMudar();
    },
    onError: (e: unknown) => setErro((e as ApiError).mensagem),
  });

  const redefinir = useMutation({
    mutationFn: () => api.post<{ senhaTemporaria: string }>(`/usuarios/${usuario.id}/redefinir-senha`),
    onSuccess: (r) => aoGerarSenha({ email: usuario.email, senha: r.senhaTemporaria }),
  });

  return (
    <>
      <tr className={`border-t border-borda-suave ${usuario.ativo ? '' : 'opacity-55'}`}>
        <td className="px-[18px] py-[13px]">
          <p className="text-[13.5px] font-bold">
            {usuario.nome}
            {souEu && <span className="ml-2 text-[11.5px] font-semibold text-texto-mudo">(você)</span>}
          </p>
          <p className="font-mono text-[12px] text-texto-suave">{usuario.email}</p>
          {erro && <p className="mt-1 text-[11.5px] font-semibold text-erro">{erro}</p>}
        </td>

        <td className="px-[18px] py-[13px]">
          {mostrarOperadora ? (
            <span className="text-[13px]">{usuario.operadora?.nome ?? '—'}</span>
          ) : usuario.tfaAtivo ? (
            <Pill tom="ok">
              <ShieldCheck size={12} className="mr-1" /> Ativo
            </Pill>
          ) : (
            <Pill tom="neutro">Sem 2FA</Pill>
          )}
        </td>

        <td className="px-[18px] py-[13px]">
          <div className="flex flex-wrap gap-1">
            {usuario.papeis.map((p) => (
              <Pill key={p.id} tom="info">
                {p.nome}
              </Pill>
            ))}
          </div>
        </td>

        <td className="px-[18px] py-[13px] text-[12.5px] text-texto-suave">
          {usuario.ultimoAcesso
            ? new Date(usuario.ultimoAcesso).toLocaleString('pt-BR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })
            : 'nunca entrou'}
        </td>

        <td className="px-[18px] py-[13px]">
          <div className="flex justify-end gap-1.5">
            <Botao tamanho="tabela" onClick={() => setEditandoPapeis(true)}>
              Papéis
            </Botao>
            <Botao tamanho="tabela" onClick={() => redefinir.mutate()} carregando={redefinir.isPending}>
              Nova senha
            </Botao>
            <Botao
              tamanho="tabela"
              variante={usuario.ativo ? 'perigo' : 'secundario'}
              onClick={() => alternarAtivo.mutate()}
              carregando={alternarAtivo.isPending}
            >
              {usuario.ativo ? 'Desativar' : 'Reativar'}
            </Botao>
          </div>
        </td>
      </tr>

      {editandoPapeis && (
        <tr>
          <td colSpan={5} className="p-0">
            <ModalPapeis
              usuario={usuario}
              papeis={papeis}
              aoFechar={() => setEditandoPapeis(false)}
              aoSalvar={aoMudar}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ModalPapeis({
  usuario,
  papeis,
  aoFechar,
  aoSalvar,
}: {
  usuario: Usuario;
  papeis: Papel[];
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [selecionados, setSelecionados] = useState<string[]>(usuario.papeis.map((p) => p.id));

  const salvar = useMutation({
    mutationFn: () => api.patch(`/usuarios/${usuario.id}/papeis`, { papelIds: selecionados }),
    onSuccess: () => {
      aoSalvar();
      aoFechar();
    },
  });

  // Papéis com acesso total não aparecem para usuários de cliente: a
  // combinação faria a conta enxergar todas as operadoras, e o servidor
  // recusaria de qualquer forma — melhor não oferecer a opção.
  const disponiveis =
    usuario.tipo === 'CLIENTE' ? papeis.filter((p) => !p.escopo.includes('*')) : papeis;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`Papéis de ${usuario.nome}`}
      descricao="As permissões são a união dos papéis escolhidos."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={selecionados.length === 0}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <ul className="space-y-2">
        {disponiveis.map((p) => (
          <li key={p.id}>
            <label className="flex cursor-pointer items-start gap-3 rounded-chip border border-borda p-3 hover:border-marca">
              <input
                type="checkbox"
                checked={selecionados.includes(p.id)}
                onChange={(e) =>
                  setSelecionados((s) =>
                    e.target.checked ? [...s, p.id] : s.filter((id) => id !== p.id),
                  )
                }
                className="mt-0.5 size-4 shrink-0 accent-marca"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-[13.5px] font-bold">
                  {p.nome}
                  {p.escopo.includes('*') && <Pill tom="aviso">acesso total</Pill>}
                </span>
                <span className="block text-[12px] leading-snug text-texto-suave">
                  {p.descricao ?? `${p.escopo.length} permissões`}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      {salvar.error && (
        <p className="mt-4 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          {(salvar.error as ApiError).mensagem}
        </p>
      )}
    </Modal>
  );
}

function ModalNovoUsuario({
  papeis,
  aoFechar,
  aoCriar,
}: {
  papeis: Papel[];
  aoFechar: () => void;
  aoCriar: (d: { email: string; senha: string }) => void;
}) {
  const [form, setForm] = useState({
    nome: '',
    email: '',
    tipo: 'INTERNO' as 'INTERNO' | 'CLIENTE',
    operadoraId: '',
    papelIds: [] as string[],
  });

  const { data: operadoras = [] } = useQuery({
    queryKey: ['operadoras'],
    queryFn: () => api.get<Operadora[]>('/operadoras'),
    enabled: form.tipo === 'CLIENTE',
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post<{ senhaTemporaria: string }>('/usuarios', {
        ...form,
        operadoraId: form.tipo === 'CLIENTE' ? form.operadoraId : undefined,
      }),
    onSuccess: (r) => {
      aoCriar({ email: form.email, senha: r.senhaTemporaria });
      aoFechar();
    },
  });

  const disponiveis =
    form.tipo === 'CLIENTE' ? papeis.filter((p) => !p.escopo.includes('*')) : papeis;

  const valido =
    form.nome.length >= 2 &&
    form.email.includes('@') &&
    form.papelIds.length > 0 &&
    (form.tipo === 'INTERNO' || form.operadoraId);

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="largo"
      titulo="Novo usuário"
      descricao="Uma senha temporária será gerada e exibida uma única vez."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            disabled={!valido}
          >
            Criar usuário
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <Rotulo>Nome</Rotulo>
            <input
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              autoFocus
              className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
            />
          </label>
          <label className="block">
            <Rotulo>E-mail</Rotulo>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
            />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <Rotulo>Tipo</Rotulo>
            <select
              value={form.tipo}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  tipo: e.target.value as 'INTERNO' | 'CLIENTE',
                  // Limpa os papéis ao trocar de tipo: os disponíveis mudam, e
                  // manter a seleção anterior levaria a um erro do servidor.
                  papelIds: [],
                  operadoraId: '',
                }))
              }
              className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
            >
              <option value="INTERNO">Equipe Consult Networks</option>
              <option value="CLIENTE">Usuário de operadora</option>
            </select>
          </label>

          {form.tipo === 'CLIENTE' && (
            <label className="block">
              <Rotulo>Operadora</Rotulo>
              <select
                value={form.operadoraId}
                onChange={(e) => setForm((f) => ({ ...f, operadoraId: e.target.value }))}
                className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
              >
                <option value="">Selecione…</option>
                {operadoras.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div>
          <Rotulo>Papéis</Rotulo>
          <ul className="space-y-2">
            {disponiveis.map((p) => (
              <li key={p.id}>
                <label className="flex cursor-pointer items-start gap-3 rounded-chip border border-borda p-3 hover:border-marca">
                  <input
                    type="checkbox"
                    checked={form.papelIds.includes(p.id)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        papelIds: e.target.checked
                          ? [...f.papelIds, p.id]
                          : f.papelIds.filter((id) => id !== p.id),
                      }))
                    }
                    className="mt-0.5 size-4 shrink-0 accent-marca"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-[13.5px] font-bold">
                      {p.nome}
                      {p.escopo.includes('*') && <Pill tom="aviso">acesso total</Pill>}
                    </span>
                    <span className="block text-[12px] leading-snug text-texto-suave">
                      {p.descricao}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        {criar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(criar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

function ModalSenhaGerada({
  dados,
  aoFechar,
}: {
  dados: { email: string; senha: string };
  aoFechar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Senha temporária"
      descricao="Anote agora — ela não será exibida de novo."
      rodape={<Botao variante="primario" onClick={aoFechar}>Já anotei</Botao>}
    >
      <div className="space-y-3">
        <div>
          <Rotulo>E-mail</Rotulo>
          <p className="font-mono text-[13.5px]">{dados.email}</p>
        </div>
        <div>
          <Rotulo>Senha</Rotulo>
          <div className="flex gap-2">
            <p className="flex-1 select-all rounded-chip bg-fundo px-3 py-2.5 font-mono text-[15px] font-bold tracking-wide">
              {dados.senha}
            </p>
            <Botao
              iconeEsquerda={<Copy size={15} />}
              onClick={() => {
                void navigator.clipboard.writeText(dados.senha);
                setCopiado(true);
              }}
            >
              {copiado ? 'Copiado' : 'Copiar'}
            </Botao>
          </div>
        </div>
        <p className="rounded-chip bg-aviso-fundo px-3 py-2 text-[12.5px] leading-snug text-aviso">
          O painel não envia esta senha por e-mail. Passe-a pelo canal que você já usa com a
          pessoa, e peça que ela troque no primeiro acesso.
        </p>
      </div>
    </Modal>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function Esqueleto() {
  return (
    <div className="space-y-6">
      {[0, 1].map((i) => (
        <div key={i} className="h-48 animate-pulse rounded-card border border-borda bg-superficie" />
      ))}
    </div>
  );
}
